// components/secure/EscrowExamPackPanel.js — Escrow Compliance → Examination pack.
//
// The IDFPR Compliance Examination Pack (GET /api/escrow/exam-pack) had a route and no
// screen. Choose a period and the escrow accounts, preview what the pack contains —
// item by item, what the portal produces and what must be produced by hand — then
// download it as a PDF. Every download is logged; the panel shows the last one.
//
// Shown to the managing broker and to a Division examiner. The server decides: an
// examiner without the "escrow" area grant is refused by middleware/examiner.js, and
// the panel then says so rather than showing an empty pack.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import EscrowExamBundleSection from './EscrowExamBundleSection';
import DataList from '../../components/shared/DataList';

const input = 'border border-slate-300 rounded px-2 py-1 text-sm';
const btn = 'px-3 py-1.5 text-sm bg-slate-800 text-white rounded disabled:bg-indigo-300';
const btn2 = 'px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700 disabled:opacity-50';
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const when = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function EscrowExamPackPanel() {
    const api = useApiClient();
    const user = getCurrentUser();
    const allowed = !!(user?.isManagingBroker || user?.role === 'examiner');
    const [accounts, setAccounts] = useState([]);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [picked, setPicked] = useState([]);   // empty = all accounts
    const [pack, setPack] = useState(null);
    const [log, setLog] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const query = useCallback(() => {
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to) q.set('to', to);
        if (picked.length) q.set('account_ids', picked.join(','));
        const s = q.toString();
        return s ? `?${s}` : '';
    }, [from, to, picked]);

    const loadLog = useCallback(() => api.get('/api/escrow/exam-pack/log').then(setLog).catch(() => {}), [api]);

    useEffect(() => {
        if (!allowed) return;
        api.get('/api/escrow/accounts')
            .then((a) => setAccounts((a || []).filter((x) => x.account_type !== 'operating')))
            .catch(() => {});
        loadLog();
    }, [api, allowed, loadLog]);

    if (!allowed) return null;

    const preview = async () => {
        setBusy(true); setError('');
        try { setPack(await api.get(`/api/escrow/exam-pack${query()}`)); }
        catch (e) { setPack(null); setError(e.status === 403 ? `${e.message} (the examination pack sits in the "Escrow and special accounts" area).` : e.message); }
        finally { setBusy(false); }
    };

    const download = async () => {
        setBusy(true); setError('');
        try {
            const r = await fetch(`/api/escrow/exam-pack/pdf${query()}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
            if (!r.ok) {
                let m = 'Could not print the examination pack.';
                try { m = (await r.json()).message || m; } catch { /* not JSON */ }
                throw new Error(m);
            }
            const name = (r.headers.get('content-disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'IDFPR-Examination-Pack.pdf';
            const url = URL.createObjectURL(await r.blob());
            const a = document.createElement('a');
            a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            loadLog();
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    return (
        <div className="bg-white rounded-lg shadow p-5 mb-6">
            <h2 className="text-lg font-semibold text-slate-700 mb-1">Examination pack</h2>
            <p className="text-sm text-slate-500 mb-3">
                The IDFPR Compliance Examination checklist, as the Division worked it in January 2018, with what the
                portal produces and what must be produced by hand. The period and accounts narrow the escrow figures;
                the office items are answered as they stand today.
            </p>

            <div className="flex flex-wrap items-end gap-3 mb-3">
                <label className="text-sm">From<br /><input type="date" className={input} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                <label className="text-sm">To<br /><input type="date" className={input} value={to} onChange={(e) => setTo(e.target.value)} /></label>
                <div className="text-sm">
                    Accounts <span className="text-slate-400">(none ticked = all escrow accounts)</span><br />
                    <div className="flex flex-wrap gap-3">
                        {accounts.map((a) => (
                            <label key={a.account_id} className="flex items-center gap-1">
                                <input type="checkbox" checked={picked.includes(a.account_id)} onChange={() => toggle(a.account_id)} />
                                {a.bank_name} — {a.account_name}
                            </label>
                        ))}
                        {!accounts.length && <span className="text-slate-400">No escrow accounts visible.</span>}
                    </div>
                </div>
            </div>
            <div className="flex gap-2 mb-3">
                <button className={btn2} disabled={busy} onClick={preview}>Preview contents</button>
                <button className={btn} disabled={busy} onClick={download}>Download PDF</button>
            </div>

            <EscrowExamBundleSection query={query} onLogged={loadLog} />

            <p className="text-xs text-slate-500 mb-2">
                {log?.last
                    ? <>Last generated {when(log.last.generated_at)} by <strong>{log.last.generated_by_name || 'unknown'}</strong>
                        {log.last.generated_by_role === 'examiner' ? ' (examiner)' : ''}
                        {' '}— {log.last.period_from || log.last.period_to ? `${log.last.period_from || 'start'} to ${log.last.period_to || 'today'}` : 'all records'}
                        {log.scope === 'own' ? ' (your own downloads only)' : ''}</>
                    : 'Not generated yet.'}
            </p>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

            {pack && (
                <div className="border-t pt-3">
                    <p className="text-sm font-medium text-slate-700">
                        {pack.scope.period_label} · {pack.items_total} items — {pack.portal_can_produce} from the portal, {pack.by_hand} by hand
                    </p>
                    <p className="text-xs text-slate-500 mb-2">{pack.note}</p>
                    <div className="mb-3"><DataList rows={pack.items} rowKey="item_key" csvName="escrow-exam-pack-items" defaultSort={{ key: 'section', dir: 'asc' }}
                        filters={[{ key: 'section', label: 'Section' }, { key: 'source', label: 'Source' }]}
                        columns={[
                            { key: 'label', label: 'Item' },
                            { key: 'section', label: 'Section', value: (i) => String(i.category).replace(/_/g, ' '), render: (i) => <span className="text-slate-500">{String(i.category).replace(/_/g, ' ')}</span> },
                            { key: 'source', label: 'Source', value: (i) => (i.portal_can_produce ? 'Portal' : 'By hand') },
                            { key: 'status_detail', label: 'Detail', render: (i) => <span className={i.portal_can_produce ? 'text-green-700' : 'text-amber-700'}>{i.status_detail}</span> },
                            { key: 'authority', label: 'Authority', render: (i) => <span className="text-xs text-slate-500">{i.authority || '—'}</span> },
                        ]} /></div>
                    {pack.accounts_summary.length > 0 && (
                        <DataList rows={pack.accounts_summary} rowKey="account_id" csvName="escrow-exam-pack-accounts" defaultSort={{ key: 'account', dir: 'asc' }}
                            columns={[
                                { key: 'account', label: 'Account', value: (a) => `${a.bank_name} — ${a.account_name} ••••${a.account_last4}`,
                                    render: (a) => <>{a.bank_name} — {a.account_name} <span className="font-mono text-xs">••••{a.account_last4}</span></> },
                                { key: 'journal_entries', label: 'Entries', type: 'number' },
                                { key: 'deposits', label: 'Deposits', type: 'number', render: (a) => fmt(a.deposits) },
                                { key: 'disbursements', label: 'Disbursements', type: 'number', render: (a) => fmt(a.disbursements) },
                                { key: 'recs', label: 'Reconciliations', value: (a) => `${a.reconciliations.length} in period${a.unbalanced_reconciliations ? `, ${a.unbalanced_reconciliations} not balanced` : ''}`,
                                    sortValue: (a) => a.reconciliations.length,
                                    render: (a) => <span className={a.unbalanced_reconciliations ? 'text-red-600' : ''}>{a.reconciliations.length} in period{a.unbalanced_reconciliations ? `, ${a.unbalanced_reconciliations} not balanced` : ''}</span> },
                            ]} />
                    )}
                </div>
            )}

            {log?.entries?.length > 1 && (
                <details className="mt-3 text-xs text-slate-600">
                    <summary className="cursor-pointer">Earlier downloads ({log.entries.length})</summary>
                    <ul className="mt-1 space-y-0.5">
                        {log.entries.map((e) => (
                            <li key={e.log_id}>{when(e.generated_at)} — {e.generated_by_name}{e.generated_by_role === 'examiner' ? ' (examiner)' : ''} — {e.period_from || e.period_to ? `${e.period_from || 'start'} to ${e.period_to || 'today'}` : 'all records'}{e.account_ids ? ` — accounts ${e.account_ids}` : ''}</li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}
